<template>
    <div 
        class="absolute mt-1 w-48 bg-white border border-gray-200 rounded-xl shadow-xl z-30 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200"
        :class="isOutgoing ? 'right-0' : 'left-0'"
    >
        <button 
            @click="$emit('reply', { id: messageId, text: messageText, sender: senderName })"
            class="w-full text-left px-4 py-2.5 hover:bg-gray-50 flex items-center space-x-3 transition-colors group"
        >
            <ReplyIcon class="h-4 w-4 text-gray-400 group-hover:text-blue-500" />
            <span class="text-sm font-medium text-gray-700">{{ $t('chat.reply') }}</span>
        </button>
        
        <button 
            v-if="!isFile"
            @click="$emit('copy', messageText)"
            class="w-full text-left px-4 py-2.5 hover:bg-gray-50 flex items-center space-x-3 transition-colors group"
        >
            <DuplicateIcon class="h-4 w-4 text-gray-400 group-hover:text-purple-500" />
            <span class="text-sm font-medium text-gray-700">{{ $t('actions.copy') }}</span>
        </button>

        <button 
            v-if="!isFile && isOutgoing && canEditActive"
            @click="$emit('edit', messageId)"
            class="w-full text-left px-4 py-2.5 hover:bg-gray-50 flex items-center space-x-3 transition-colors group"
        >
            <PencilIcon class="h-4 w-4 text-gray-400 group-hover:text-amber-500" />
            <span class="text-sm font-medium text-gray-700">{{ $t('actions.edit') }}</span>
        </button>

        <button 
            v-if="isFile"
            @click="$emit('download', fileName)"
            class="w-full text-left px-4 py-2.5 hover:bg-gray-50 flex items-center space-x-3 transition-colors group"
        >
            <DownloadIcon class="h-4 w-4 text-gray-400 group-hover:text-green-500" />
            <span class="text-sm font-medium text-gray-700">{{ $t('actions.download') }}</span>
        </button>

        <button 
            v-if="isOutgoing"
            @click="$emit('info', messageId)"
            class="w-full text-left px-4 py-2.5 hover:bg-gray-50 flex items-center space-x-3 transition-colors group"
        >
            <InformationCircleIcon class="h-4 w-4 text-gray-400 group-hover:text-blue-500" />
            <span class="text-sm font-medium text-gray-700">{{ $t('chat.messageInfo') }}</span>
        </button>

        <div class="border-t border-gray-100">
            <div class="px-4 py-2 text-[10px] font-bold text-gray-400 uppercase tracking-widest">{{ $t('chat.react') }}</div>
            <div class="px-4 pb-2 flex justify-between items-center">
                <button 
                    v-for="emoji in ['👍', '❤️', '😂', '😢']" 
                    :key="emoji" 
                    @click="$emit('react', { id: messageId, emoji })" 
                    class="hover:scale-125 transition-transform text-lg"
                >
                    {{ emoji }}
                </button>
                <button 
                    @click="$emit('openEmojiPicker', messageId)" 
                    class="hover:scale-125 transition-transform text-lg"
                >
                    <PlusIcon class="h-5 w-5 text-gray-400" />
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import { 
    ReplyIcon, 
    DuplicateIcon, 
    DownloadIcon, 
    PlusIcon,
    InformationCircleIcon,
    PencilIcon 
} from "@heroicons/vue/outline";

export default {
    name: 'MessageMenu',
    components: {
        ReplyIcon,
        DuplicateIcon,
        DownloadIcon,
        PlusIcon,
        InformationCircleIcon,
        PencilIcon
    },
    props: {
        messageId: {
            type: [Number, String],
            required: true
        },
        messageText: {
            type: String,
            default: ""
        },
        senderName: {
            type: String,
            default: "You"
        },
        isOutgoing: {
            type: Boolean,
            default: false
        },
        isFile: {
            type: Boolean,
            default: false
        },
        fileName: {
            type: String,
            default: ""
        },
        sentAt: {
            type: String,
            default: null
        },
        canEdit: {
            type: Boolean,
            default: true
        }
    },
    computed: {
        canEditActive() {
            // Check global edit mode first
            if (this.$page.props.chat_msg_edit_mode !== "On") return false;

            // First check: Did the backend say it's too old at load time?
            if (this.canEdit === false) return false;

            const timeLimitMinutes = this.$page.props.chat_msg_edit_time_in_minute || 5;

            if (!this.sentAt) return false;
            const diffInMinutes = (new Date() - new Date(this.sentAt)) / (1000 * 60);
            return diffInMinutes <= timeLimitMinutes;
        }
    },
    emits: ["reply", "copy", "react", "download", "openEmojiPicker", "edit", "info"]
};
</script>
